import styled, { CSSObject } from "styled-components";
// deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ITheme } from "@swiggy-private/dls-web-common/dist/components/types.internal";
// utils
import {
    STATE_CHANGE_DELAY,
    HALF_RAY_BACKGROUND,
    RAYS_BACKGROUND_URL,
    XP2_MASTHEAD,
} from "@super-ui/components/m2c-swiggy-one-halfcard/constants";
import { getBackgroundImage } from "@super-ui/components/m2c-swiggy-one-halfcard/helpers";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

const SEPARATION_LINE_BACKGROUND =
    "linear-gradient(90deg, rgba(255, 255, 255, 0.43) 0%, rgba(2, 6, 12, 0.15) 50%, rgba(255, 255, 255, 0.43) 100%)";
const SUBTITLES_BACKGROUND =
    "linear-gradient(90deg, rgba(255, 255, 255, 0.85) 0%, #FFEEE5 50%, rgba(255, 255, 255, 0.85) 100%);";

const getBackgroundGradient = (theme: ITheme) =>
    `linear-gradient(270deg, #EEA39E 0%, ${theme.colors[theme.colorMode].background_Primary} 100%)`;

const getVariantSpecificStyles = (isVariantV1: boolean, theme: ITheme, isOneLite?: boolean) => {
    if (isVariantV1) {
        const backgroundImageId = isOneLite ? HALF_RAY_BACKGROUND : RAYS_BACKGROUND_URL;
        return {
            // For PLUS program type (non One Lite), use rays background image
            backgroundImage: getBackgroundImage(backgroundImageId, { width: 500 }),
            backgroundSize: "126%",
            backgroundPositionX: "center",
            backgroundPositionY: "-75px",
            backgroundRepeat: "no-repeat",
            paddingTop: `calc(${isOneLite ? 1 : 2} * ${theme.spacing.xLarge})`,
        };
    }
    return {
        paddingTop: 0,
    };
};

export const BullsEyeHalfCard = styled.div<{ isOneLite?: boolean; isVariantV1: boolean }>(
    ({ isOneLite, isVariantV1, theme }) => {
        return {
            padding: `${theme.spacing.medium}`,
            paddingBottom: `calc(var(--sab, 0px) + ${theme.spacing.medium})`,
            backgroundColor: theme.colors[theme.colorMode].background_Primary,
            borderRadius: `${theme.cornerRadius.xLarge} ${theme.cornerRadius.xLarge} 0px 0px`,
            boxShadow: "0px -4px 16px rgba(0, 0, 0, 0.1)",
            transition: `${STATE_CHANGE_DELAY}ms`,
            ...getVariantSpecificStyles(isVariantV1, theme, isOneLite),
        };
    },
);

export const HalfCard = styled.div(({ theme }) => ({
    paddingTop: theme.spacing.medium,
}));

const floatAnimation = {
    animation: "float 2s cubic-bezier(0.4, 0, 1, 1) infinite",
    "@keyframes float": {
        "0%": { transform: "translate(-48%, 0)" },
        "50%": { transform: "translate(-48%, -5px)" },
        "100%": { transform: "translate(-48%, 0)" },
    },
};

export const DEContainer = styled.div(() => ({
    marginTop: "-140px",
    position: "relative" as CSSObject["position"],
    display: "flex",
    justifyContent: "center",
    maxHeight: "190px",
}));

export const DEAsset = styled.div(() => ({
    width: "134px",
    position: "absolute" as CSSObject["position"],
    left: "50%",
    top: "60px",
    ...floatAnimation,
}));

export const SwiggyLogo = styled.div(() => ({
    width: "100px",
    height: "42px",
    position: "absolute" as CSSObject["position"],
    top: "-18px",
    left: "50%",
    transform: "translateX(-50%)",
}));

export const MastHeadLogo = styled.div(({ theme }) => ({
    width: "100vw",
    height: "46.4vw",
    backgroundImage: getBackgroundImage(XP2_MASTHEAD, { width: 500 }),
    backgroundSize: "cover",
    paddingTop: theme.spacing.xxLarge,
    marginBottom: theme.spacing.xxSmall,
    marginLeft: "50%",
    transform: "translateX(-50%)",
}));

export const HalfCardContainer = styled.div(() => ({
    width: "100%",
}));

export const DefaultHeaderContainer = styled.div(({ theme }) => ({
    marginBottom: theme.spacing.xxSmall,
}));

export const HalfCardDetails = styled.div(() => ({
    width: "90%",
    margin: "auto",
    textAlign: "center" as CSSObject["textAlign"],
}));

export const TitlePlan = styled.span`
    color: ${(props) => props.theme.colors[props.theme.colorMode].primary};
`;

export const SubTitles = styled(Text)(({ theme }) => ({
    width: "100vw",
    marginLeft: "50%",
    marginTop: theme.spacing.small,
    transform: "translate(-50%)",
    padding: `${theme.spacing.xSmall} ${theme.spacing.large}`,
    background: SUBTITLES_BACKGROUND,
}));

export const TagLineHeader = styled.div(({ theme }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing.xLarge,
    marginBottom: theme.spacing.small,
}));

export const NoteVector = styled.div<{ isRight?: boolean }>(({ isRight = true, theme }) => ({
    width: "80px",
    height: "1.5px",
    background: getBackgroundGradient(theme),
    transform: isRight ? "rotate(180deg)" : "rotate(0deg)",
}));

export const SeparationLine = styled.div(({ theme }) => ({
    width: "70%",
    height: "2px",
    margin: "auto",
    marginTop: theme.spacing.small,
    marginBottom: theme.spacing.medium,
    background: SEPARATION_LINE_BACKGROUND,
}));

export const TagLine = styled.div(({ theme }) => ({
    padding: `${theme.spacing.xxSmall} ${theme.spacing.xSmall}`,
    borderRadius: theme.cornerRadius.xxSmall,
    margin: `auto ${theme.spacing.xxSmall}`,
    backgroundColor: theme.colors[theme.colorMode].primary_Sub,
}));

export const TagLineText = styled(Text)(() => ({
    width: "max-content",
}));

export const CtaContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing.xxLarge,
    marginBottom: theme.spacing.small,
}));

export const PrimaryButton = styled(SuperButton)(() => ({
    background: "#FF5200", // adding hardcoded color to avoid theme change in IM
}));

export const SkipButton = styled.div(({ theme }) => ({
    marginTop: theme.spacing.large,
    display: "flex",
    justifyContent: "center",
}));

export const ContainerXp2 = styled.div(({ theme }) => ({
    marginTop: `calc(${theme.spacing.xxxSmall} * -1)`,
    paddingBottom: theme.spacing.xSmall,
    textAlign: "center",
}));
